import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppState } from 'src/app/app.state';
import { OwnerModel } from 'src/app/model/ownerModel.model';
import { ParcelModel } from 'src/app/model/parcelModel.model';
import { AddressModel } from 'src/app/modules/shared/model/addressModel.model';
import { SessionCapModelService } from 'src/app/modules/shared/services/session-cap-model.service';
import { SettingService } from 'src/app/modules/shared/services/setting.service';
import { DataSourceFlag, FlagEnum, PageLayoutMode } from 'src/app/repo/enum.repo';
import { selectAppSettings } from 'src/app/state/selectors/app-settings.selectors';
import { sortByProperty } from 'src/app/util/sortUtil';
import { RefAddressModel } from '../../../../model/refAddressModel.model';
import { APOEntityType } from '../../../../repo/contact-process-type.repo';
import { isStringNullOrWhiteSpace } from '../../../../util/StringUtil';
import { Column } from '../../../shared/model/column.model';
import { EditAddressFormComponent } from '../../components/edit-address-form/edit-address-form.component';
import { EditOwnerFormComponent } from '../../components/edit-owner-form/edit-owner-form.component';
import { EditParcelFormComponent } from '../../components/edit-parcel-form/edit-parcel-form.component';
import { ApoQueryable } from '../../models/apoRecord.model';
import { APOSearchOutputList } from '../../models/APOSearchResult.model';
import { ApoSuggestion } from '../../models/apoSuggestion.model';
import { APOSuggestionData } from '../../models/ApoSuggestionData.model';
import { APOSearchService } from '../../services/aposearch-service.service';

@Component({
  selector: 'multi-apo-screen',
  templateUrl: './multi-apo-screen.component.html',
  styleUrls: ['./multi-apo-screen.component.less']
})
export class MultiApoScreenComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('addressLayout') addressFormLayout: EditAddressFormComponent;
  @ViewChild('parcelLayout') parcelFormLayout: EditParcelFormComponent;
  @ViewChild('ownerLayout') ownerFormLayout: EditOwnerFormComponent;

  module: string;
  compOrder = ['A', 'P', 'O'];
  xapoMode: boolean;
  adminMode: boolean;
  processing = false;
  pageLayoutModes = PageLayoutMode;
  sourceType: string; 

  addressCards = new Array<AddressModel>();
  selectedAddress: AddressModel;
  addressMode: PageLayoutMode;
  addressRequired: boolean;
  showAddressForm: boolean;
  addressType: string;
  addressMax: number;

  parcelCards = new Array<ParcelModel>();
  selectedParcel: ParcelModel;
  parcelMode: PageLayoutMode;
  parcelRequired: boolean;
  showParcelForm: boolean;
  parcelType: string;
  parcelMax: number;

  ownerCards = new Array<OwnerModel>();
  selectedOwner: OwnerModel;
  ownerMode: PageLayoutMode;
  ownerRequired: boolean;
  showOwnerForm: boolean;
  ownerType: string;
  ownerMax: number;

  protected destroyActions = new Subject<boolean>();
  constructor(private store: Store<AppState>, private route: ActivatedRoute, private capModelService: SessionCapModelService,
    private messageService: MessageService, private settingsService: SettingService) {
    this.route.queryParams.subscribe(params => {
      this.module = params['Module'];
      this.addressMax = params['addressMax'] ?? 1;
      this.parcelMax = params['parcelMax'] ?? 1;
      this.ownerMax = params['ownerMax'] ?? 1;
      this.addressType = params['addressType'];
      this.parcelType = params['parcelType'];
      this.ownerType = params['ownerType'];
      this.addressRequired = (params['addressRequired'] === 'Y');
      this.parcelRequired = (params['parcelRequired'] === 'Y');
      this.ownerRequired = (params['ownerRequired'] === 'Y');

      this.compOrder = [...params['compOrder']];
     
    });
  }

  ngOnInit(): void {
    this.store.select(selectAppSettings)
      .pipe(takeUntil(this.destroyActions))
      .subscribe({
        next: state => {
          this.adminMode = state.AdminMode;
          this.addressMode = PageLayoutMode.ADMIN;
          this.ownerMode = PageLayoutMode.ADMIN;
          this.parcelMode = PageLayoutMode.ADMIN;
        }
      });

    this.settingsService.isXAPOAgency()
      .pipe(takeUntil(this.destroyActions))
      .subscribe(isXapo => {
        this.xapoMode = isXapo;

        this.capModelService.getAddressesFromCapModel(this.module)
          .pipe(takeUntil(this.destroyActions))
          .subscribe((res: AddressModel[]) => {
            if (res && res.length > 0) {
              //reset addresses to match Cap Model in session
              sortByProperty(res, 'primaryFlag', 'DESC');
              this.addressCards = res;
              //Set a primary record if none already provided
              this.setPrimaryAddressIfNeeded();
            }
          }, error => {
            this.handleError(error);
          });

        this.capModelService.getParcelsFromCapModel(this.module)
          .pipe(takeUntil(this.destroyActions))
          .subscribe((res: ParcelModel[]) => {
            if (res && res.length > 0) {
              //reset parcels to match Cap Model in Session
              sortByProperty(res, 'primaryParcelFlag', 'DESC');
              this.parcelCards = res;
              //Set a primary record if none already provided
              this.setPrimaryParcelIfNeeded();
            }
          }, error => {
            this.handleError(error);
          });

        this.capModelService.getOwnersFromCapModel(this.module)
          .pipe(takeUntil(this.destroyActions))
          .subscribe((res: OwnerModel[]) => {
            if (res && res.length > 0) {
              //reset owners to match Cap Model in Session
              sortByProperty(res, 'isPrimary', 'DESC');
              this.ownerCards = res;
              //Set a primary record if none already provided
              this.setPrimaryOwnerIfNeeded();
            }
          }, error => {
            this.handleError(error);
          });
      });

  }

  ngAfterViewInit(): void {
    window.parent.postMessage('CommunityView:Resize', '*');
  }

  ngOnDestroy(): void {
    this.destroyActions.next();
    this.destroyActions.complete();
  }

  handleError(error: any) {
    if (typeof error !== 'string') {
      error = error.error;
    }
    this.messageService.clear();
    this.messageService.add({
      severity: 'error',
      summary: error,
      closable: false
    });
  }

  /* Primary Record Handling */

  setPrimaryAddressIfNeeded() {
    if (this.addressCards.length > 0) {
      if (!(this.addressCards.find((addr) => addr.primaryFlag === FlagEnum.Y))) {
        this.addressCards[0].primaryFlag = FlagEnum.Y;
      }
    }
  }

  setPrimaryParcelIfNeeded() {
    if (this.parcelCards.length > 0) {
      if (!(this.parcelCards.find((parcel) => parcel.primaryParcelFlag === FlagEnum.Y))) {
        this.parcelCards[0].primaryParcelFlag = FlagEnum.Y;
      }
    }
  }

  setPrimaryOwnerIfNeeded() {
    if (this.ownerCards.length > 0) {
      if (!(this.ownerCards.find((owner) => owner.isPrimary === FlagEnum.Y))) {
        this.ownerCards[0].isPrimary = FlagEnum.Y;
      }
    }
  }

  setPrimaryAddress(primaryRecord: AddressModel) {
    //Setting PrimaryFlag as 'N' to all addresses
    this.addressCards.forEach((card) => {
      card.primaryFlag = FlagEnum.N;
    });
    //Setting PrimaryFlag as 'Y' to current Address
    primaryRecord.primaryFlag = FlagEnum.Y;

    //Saving Address Data to capModel
    this.saveChanges(APOEntityType.Address);
  }

  suggestionsFoundClick(suggestionRecord: ApoSuggestion) {
    if (suggestionRecord?.addresses.length === 1) {
      this.addAddresses(suggestionRecord.addresses);
    }
    if (suggestionRecord?.parcels.length === 1) {
      this.addParcels(suggestionRecord.parcels);
    }
    if (suggestionRecord?.owners.length === 1) {
      this.addOwners(suggestionRecord.owners);
    }
  }

  setPrimaryParcel(primaryRecord: ParcelModel) {
    //Setting PrimaryFlag as 'N' to all addresses
    this.parcelCards.forEach((card) => {
      card.primaryParcelFlag = FlagEnum.N;
    });

    //Setting PrimaryFlag as 'Y' to current Address
    primaryRecord.primaryParcelFlag = FlagEnum.Y;

    //Saving Address Data to capModel
    this.saveChanges(APOEntityType.Address);
  }

  setPrimaryOwner(primaryRecord: OwnerModel) {
    //Setting PrimaryFlag as 'N' to all addresses
    this.ownerCards.forEach((card) => {
      card.isPrimary = FlagEnum.N;
    });
    //Setting PrimaryFlag as 'Y' to current Address
    primaryRecord.isPrimary = FlagEnum.Y;

    //Saving Address Data to capModel
    this.saveChanges(APOEntityType.Owner);
  }

  /* Add,Search launch links */
  addAddressClick(event: Event) {
    if (this.adminMode || this.processing) {
      return;
    }
    this.processing = true;
    if (this.addressMax > this.addressCards.length) {
     
      this.addressMode = PageLayoutMode.EDIT;
      const addressModel = new AddressModel();
      this.addressCards.push(addressModel);
      this.selectedAddress = addressModel;
      this.addressFormLayout.updateAddressFormInstance(addressModel);
      this.showAddressForm = true;
    } else {
      this.messageService.clear();
      this.messageService.add({
        severity: 'error',
        summary: $localize`A maximum of ${this.addressMax} address(es) can be added to this record.`,
        closable: false
      });
    }
    this.processing = false;
  }

  searchAddressClick(event: Event) {
    if (this.adminMode || this.processing) {
      return;
    }

    this.processing = true;
    if (this.addressMax > this.addressCards.length) {
      this.sourceType = APOEntityType.Address;
      this.addressMode = PageLayoutMode.SEARCH;
      this.addressFormLayout.updateAddressFormInstance(new AddressModel());
      this.showAddressForm = true;
    } else {
      this.messageService.clear();
      this.messageService.add({
        severity: 'error',
        summary: $localize`A maximum of ${this.addressMax} address(es) can be added to this record.`,
        closable: false
      });
    }
    this.processing = false;
  }

  viewAddressClick(addressModel: AddressModel) {
    if (this.adminMode) {
      return;
    }

    this.processing = true;
    this.selectedAddress = addressModel;
    this.addressFormLayout.updateAddressFormInstance(addressModel);
    this.addressMode = PageLayoutMode.VIEW;
    this.showAddressForm = true;
    this.processing = false;
  }


  addParcelClick(event: Event) {
    if (this.adminMode || this.processing) {
      return;
    }
    
    const parcelModel = new ParcelModel();
    this.parcelCards.push(parcelModel);
    this.selectedParcel = parcelModel;
    this.parcelFormLayout.updateFormInstance(parcelModel);
    this.showParcelForm = true;
  }

  searchParcelClick(event: Event) {
    if (this.adminMode || this.processing) {
      return;
    }

    this.processing = true;
    if (this.parcelMax > this.parcelCards.length) {
      this.sourceType = APOEntityType.Parcel;
      this.parcelMode = PageLayoutMode.SEARCH;
      this.parcelFormLayout.updateFormInstance(new ParcelModel());
      this.showParcelForm = true;
    } else {
      this.messageService.clear();
      this.messageService.add({
        severity: 'error',
        summary: $localize`A maximum of ${this.parcelMax} parcel(s) can be added to this record.`,
        closable: false
      });
    }
    this.processing = false;
  }

  viewParcelClick(parcelModel: ParcelModel) {

    if (this.adminMode) {
      return;
    }

    this.processing = true;
    this.selectedParcel = parcelModel;
    this.parcelFormLayout.updateFormInstance(parcelModel);
    this.parcelMode = PageLayoutMode.VIEW;
    this.showParcelForm = true;
    this.processing = false;
  }


  addOwnerClick(event: Event) {
    if (this.adminMode || this.processing) {
      return;
    }
   
    const ownerModel = new OwnerModel();
    this.ownerCards.push(ownerModel);
    this.selectedOwner = ownerModel;
    this.ownerFormLayout.updateFormInstance(ownerModel);
    this.showOwnerForm = true;
  }

  searchOwnerClick(event: Event) {
    if (this.adminMode || this.processing) {
      return;
    }

    this.processing = true;
    if (this.ownerMax > this.ownerCards.length) {
      this.sourceType = APOEntityType.Owner;
      this.ownerMode = PageLayoutMode.SEARCH;
      this.ownerFormLayout.updateFormInstance(new OwnerModel());
      this.showOwnerForm = true;
    } else {
      this.messageService.clear();
      this.messageService.add({
        severity: 'error',
        summary: $localize`A maximum of ${this.ownerMax} owner(s) can be added to this record.`,
        closable: false
      });
    }
    this.processing = false;
  }

  viewOwnerClick(ownerModel: OwnerModel) {

    if (this.adminMode) {
      return;
    }

    this.processing = true;
    this.selectedOwner = ownerModel;
    this.ownerFormLayout.updateFormInstance(ownerModel);
    this.ownerMode = PageLayoutMode.VIEW;
    this.showOwnerForm = true;
    this.processing = false;
  }

  editRecordClick(record: ApoQueryable) {
    if (this.adminMode || this.processing) {
      return;
    }

    if (record.hasOwnProperty('addressId')) {
      this.selectedAddress = record as AddressModel;
      this.showAddressForm = true;
    } else if (record.hasOwnProperty('parcelNumber')) {
      this.selectedParcel = record as ParcelModel;
      this.showParcelForm = true;
    } else if (record.hasOwnProperty('ownerNumber')) {
      this.selectedOwner = record as OwnerModel;
      this.showOwnerForm = true;
    }
  }

  /* Add Methods */
  addAddress(addr: AddressModel) {
    if (this.adminMode || this.processing) {
      return;
    }
    let idx = -1;
    if (this.xapoMode) {
      idx = this.addressCards.findIndex((address) => address.UID === addr.UID);

    } else {
      idx = this.addressCards.findIndex((address) => address.addressId === addr.addressId);
    }
    
    if (idx >= 0) {
      this.addressCards[idx] = addr; //replace records
    } else {
      this.addressCards.push(addr);
    }
    this.setPrimaryAddressIfNeeded();
  }

  addParcel(prcl: ParcelModel) {
    if (this.adminMode || this.processing) {
      return;
    }
    
    let idx = -1;
    if (this.xapoMode) {
      idx = this.parcelCards.findIndex((parcel) => parcel.UID === prcl.UID);
    } else {
      idx = this.parcelCards.findIndex((parcel) => parcel.parcelNumber === prcl.parcelNumber);
    }

    if (idx >= 0) {
      this.parcelCards[idx] = prcl; //replace records
    } else {
      this.parcelCards.push(prcl);
    }
    this.setPrimaryParcelIfNeeded();

  }

  addOwner(ownr: OwnerModel) {
    if (this.adminMode || this.processing) {
      return;
    }

    let idx = -1;
    if (this.xapoMode) {
      idx = this.ownerCards.findIndex((owner) => owner.UID === ownr.UID);
    } else {
      idx = this.ownerCards.findIndex((owner) => owner.ownerNumber === ownr.ownerNumber);
    }

    if (idx >= 0) {
      this.ownerCards[idx] = ownr; //replace records
    } else {
      this.ownerCards.push(ownr);
    }
    this.setPrimaryOwnerIfNeeded();
  }

  /* Delete / Remove methods */
  deleteRecordClick(record: ApoQueryable) {
    if (this.adminMode || this.processing) {
      return;
    }

    let isRecordDeleted = false;
    let apoEntityType;

    if (record.hasOwnProperty('addressId') || record.hasOwnProperty('refAddressId')) {
      apoEntityType = APOEntityType.Address;
      this.selectedAddress = null;
      this.showAddressForm = false;
      const addr = record as AddressModel;
      let idx = -1;
      if (this.xapoMode) {
        idx = this.addressCards.findIndex((address) => address.UID === addr.UID);
      } else {
        idx = this.addressCards.findIndex((address) => address.addressId === addr.addressId);
      }
      const primaryRecordDeleted = addr.primaryFlag === FlagEnum.Y;
      if (idx >= 0) {
        this.addressCards.splice(idx, 1);
        isRecordDeleted = true;
      }
      //set primary when detlete the primary address
      if (primaryRecordDeleted && this.addressCards.length > 0) {
        this.setPrimaryAddressIfNeeded();
      }
    } else if (record.hasOwnProperty('parcelNumber')) {
      apoEntityType = APOEntityType.Parcel;
      this.selectedParcel = null;
      this.showParcelForm = false;
      const prcl = record as ParcelModel;
      let idx = -1;
      if (this.xapoMode) {
        idx = this.parcelCards.findIndex((parcel) => parcel.UID === prcl.UID);
      } else {
        idx = this.parcelCards.findIndex((parcel) => parcel.parcelNumber === prcl.parcelNumber);
      }
      const primaryRecordDeleted = prcl.primaryParcelFlag === FlagEnum.Y;
      if (idx >= 0) {
        this.parcelCards.splice(idx, 1);
        isRecordDeleted = true;
      }
      //set primary when detlete the primary address
      if (primaryRecordDeleted && this.parcelCards.length > 0) {
        this.setPrimaryParcelIfNeeded();
      }

    } else if (record.hasOwnProperty('ownerNumber')) {
      apoEntityType = APOEntityType.Owner;
      this.selectedOwner = null;
      this.showOwnerForm = false;

      const ownr = record as OwnerModel;
      let idx = -1;
      if (this.xapoMode) {
        idx = this.ownerCards.findIndex((owner) => owner.UID === ownr.UID);
      } else {
        idx = this.ownerCards.findIndex((owner) => owner.ownerNumber === ownr.ownerNumber);
      }
      const primaryRecordDeleted = ownr.isPrimary === FlagEnum.Y;
      if (idx >= 0) {
        this.ownerCards.splice(idx, 1);
        isRecordDeleted = true;
      }
      //set primary when detlete the primary address
      if (primaryRecordDeleted && this.ownerCards.length > 0) {
        this.setPrimaryOwnerIfNeeded();
      }

    } else {
      console.log('Error: Unable to identify record for deletion');
      console.log(record);
    }
    //Saving Data to capModel when APO is deleted
    if (isRecordDeleted) {
      this.saveChanges(apoEntityType);
    }
  }

  /* Record Filtering */
  //if APO, check to see if addressId present and compare - else compare refAddressId
  filterAddress =
    (addr: AddressModel, index: number, arry: Array<AddressModel>): boolean =>
      this.xapoMode
        ? arry.findIndex(s => s.UID === addr.UID) < 0
        : (addr.addressId
          ? arry.findIndex(s => (s.addressId === addr.addressId)) < 0
          : arry.findIndex(s => s.refAddressId === addr.refAddressId) < 0);

  //if APO, compare parcelNumber
  filterParcels =
    (prcl: ParcelModel, index: number, arry: Array<ParcelModel>): boolean =>
      this.xapoMode
        ? arry.findIndex(s => s.UID === prcl.UID) < 0
        : arry.findIndex(s => s.parcelNumber === prcl.parcelNumber) < 0;

  //if APO, compare ownerNumber
  filterOwners =
    (ownr: OwnerModel, index: number, arry: Array<OwnerModel>): boolean =>
      this.xapoMode
        ? arry.findIndex(s => s.UID === ownr.UID) < 0
        : arry.findIndex(s => s.ownerNumber === ownr.ownerNumber) < 0;

  /* Cross Page Messaging */
  resize(event) {
    window.parent.postMessage('CommunityView:ResizeDialog', '*');
  }

  collapseSize(event) {
    window.parent.postMessage('CommunityView:RemoveMin', '*');
  }

  /* UI Operations */
  closeAddressDialog(event: boolean) {
    this.showAddressForm = false;
  }

  closeParcelDialog(event: boolean) {
    this.showParcelForm = false;
  }

  closeOwnerDialog(event: boolean) {
    this.showOwnerForm = false;
  }

  /* Save Operations */
  addAddresses(newAddresses: AddressModel[]) {
    newAddresses.forEach(addr => {
      let idx = -1;
      if (this.xapoMode) {
        idx = this.addressCards.findIndex((a) => a.UID === addr.UID);
      }
      else {
        if (addr.refAddressId) {
          idx = this.addressCards.findIndex((a) => a.refAddressId === addr.refAddressId);
        }
        else if(addr.addressId){
          idx = this.addressCards.findIndex((a) => a.addressId === addr.addressId);
        }
      }
      if (idx >= 0) {
        this.addressCards.splice(idx, 1);
      }
      this.addressCards.push(addr);
    });
    this.showAddressForm = false;
    this.setPrimaryAddressIfNeeded();
    this.saveChanges(APOEntityType.Address);
  }

  addParcels(newparcels: ParcelModel[]) {
    newparcels.forEach(prcl => {
      let idx = -1;
      if (this.xapoMode) {
        idx = this.parcelCards.findIndex((p) => p.UID === prcl.UID);
      } else {
        idx = this.parcelCards.findIndex((p) => p.parcelNumber === prcl.parcelNumber);
      }
      if (idx >= 0) {
        this.parcelCards.splice(idx, 1);
      }
      this.parcelCards.push(prcl);
    });
    this.showParcelForm = false;
    this.setPrimaryParcelIfNeeded();
    this.saveChanges(APOEntityType.Parcel);
  }

  addOwners(newOwners: OwnerModel[]) {
    newOwners.forEach(own => {
      let idx = -1;
      if (this.xapoMode) {
        idx = this.ownerCards.findIndex(o => o.UID === own.UID);
      } else {
        idx = this.ownerCards.findIndex(o => o.ownerNumber === own.ownerNumber);
      }
      if (idx >= 0) {
        this.ownerCards.splice(idx, 1);
      }
      this.ownerCards.push(own);
    });
    this.showOwnerForm = false;
    this.setPrimaryOwnerIfNeeded();
    this.saveChanges(APOEntityType.Owner);
  }

  saveChanges(apoEntityType) {
    if (this.adminMode || this.processing) {
      return;
    }
    let result = true;
    if (apoEntityType === APOEntityType.Address) {
      this.capModelService.saveAddressesToCapModel(this.addressCards, this.module)
        .pipe(takeUntil(this.destroyActions))
        .subscribe(res => {
          if (res !== 'success') {
            result = false;
          }
        }, error => {
          this.handleError(error);
        });
    }

    if (apoEntityType === APOEntityType.Parcel) {
      this.capModelService.saveParcelsToCapModel(this.parcelCards, this.module)
        .pipe(takeUntil(this.destroyActions))
        .subscribe(res => {
          if (res !== 'success') {
            result = false;
          }
        });
    }

    if (apoEntityType === APOEntityType.Owner) {
      this.capModelService.saveOwnersToCapModel(this.ownerCards, this.module)
        .pipe(takeUntil(this.destroyActions))
        .subscribe(res => {
          if (res !== 'success') {
            result = false;
          }
        }, error => {
          this.handleError(error);
        });
    }
    this.messageService.clear();

    if (result) {
      this.messageService.add({
        severity: 'success',
        summary: $localize`Your changes were applied successfully.`,
        closable: false
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: $localize`An Error occured while applying your changes. Please try again.`,
        closable: false
      });
    }
  }
}
